import axios from 'axios';
import { Song } from '../types';

// Search for songs
export const searchSongs = async (query: string): Promise<Song[]> => {
  try {
    const response = await axios.get(`/api/youtube/search?q=${encodeURIComponent(query)}`);
    return response.data;
  } catch (error) {
    console.error('Error searching for songs:', error);

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.error;
      if (typeof message === 'string') {
        throw new Error(message);
      }
    }

    throw new Error('Error searching for songs');
  }
};

export interface ResolvedSong extends Song {
  isAgeRestricted?: boolean; // undefined when the backend could not determine it
  startSeconds?: number; // parsed from a "t=" param in the pasted link, if any
  source?: 'api' | 'oembed';
}

// Resolve a pasted YouTube link (or bare video ID) into song metadata.
// This bypasses the YouTube search and keeps working when the search quota is exhausted.
export const resolveSongFromLink = async (url: string): Promise<ResolvedSong> => {
  try {
    const response = await axios.get(`/api/youtube/resolve?url=${encodeURIComponent(url)}`);
    return response.data;
  } catch (error) {
    console.error('Error resolving YouTube link:', error);

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.error;
      if (typeof message === 'string') {
        throw new Error(message);
      }
    }

    throw new Error('Could not load that YouTube link');
  }
};

// Check if a video is age-restricted
export const checkVideoAgeRestriction = async (videoId: string): Promise<boolean> => {
  try {
    const response = await axios.get(`/api/youtube/age-restriction/${videoId}`);
    return response.data.isAgeRestricted;
  } catch (error) {
    console.error('Error checking video age restriction:', error);
    // If we can't determine, assume it might be restricted to be safe
    return true;
  }
};

export async function sanitizeSongTitle(title: string): Promise<string> {
  try {
    const response = await fetch('/api/sanitize-title', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ title }),
    });

    if (!response.ok) {
      throw new Error('Failed to sanitize song title');
    }

    const data = await response.json();
    return data.sanitizedTitle;
  } catch (error) {
    console.error('Error sanitizing song title:', error);
    return title; // Return original title as fallback
  }
}
